import React, { useState, useCallback, useRef, useEffect } from 'react';
import { Upload, Download, AlertCircle, Globe, RotateCcw, Image as ImageIcon, DownloadCloud } from 'lucide-react';

type BackgroundType = 'transparent' | 'white' | 'custom';

const FAVICON_SIZES = [16, 32, 48, 64, 128, 192, 512];

function formatFileSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

const FaviconGenerator: React.FC = () => {
  const [file, setFile] = useState<File | null>(null);
  const [bgType, setBgType] = useState<BackgroundType>('transparent');
  const [bgColor, setBgColor] = useState('#ffffff');
  const [cornerRadius, setCornerRadius] = useState(0);
  const [error, setError] = useState<string | null>(null);
  const [imgElement, setImgElement] = useState<HTMLImageElement | null>(null);
  const [imageDimensions, setImageDimensions] = useState<{ w: number; h: number } | null>(null);
  const [faviconDataUrls, setFaviconDataUrls] = useState<Map<number, string>>(new Map());
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleFileSelect = useCallback((selectedFiles: FileList | null) => {
    if (!selectedFiles || selectedFiles.length === 0) return;
    const f = selectedFiles[0];
    if (!f.type.startsWith('image/')) {
      setError('Please select a valid image file.');
      return;
    }
    setFile(f);
    setError(null);
    const reader = new FileReader();
    reader.onload = (e) => {
      const url = e.target?.result as string;
      const img = new window.Image();
      img.onload = () => {
        setImgElement(img);
        setImageDimensions({ w: img.naturalWidth, h: img.naturalHeight });
      };
      img.onerror = () => setError('Failed to load image.');
      img.src = url;
    };
    reader.readAsDataURL(f);
  }, []);

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    handleFileSelect(e.dataTransfer.files);
  }, [handleFileSelect]);

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
  }, []);

  const generateFavicon = useCallback((img: HTMLImageElement, size: number): string => {
    const canvas = document.createElement('canvas');
    canvas.width = size;
    canvas.height = size;
    const ctx = canvas.getContext('2d');
    if (!ctx) return '';

    // Background
    if (bgType !== 'transparent') {
      ctx.fillStyle = bgType === 'white' ? '#ffffff' : bgColor;
      ctx.fillRect(0, 0, size, size);
    }

    // Corner rounding
    if (cornerRadius > 0) {
      ctx.save();
      const r = (cornerRadius / 100) * (size / 2);
      ctx.beginPath();
      ctx.moveTo(r, 0);
      ctx.lineTo(size - r, 0);
      ctx.quadraticCurveTo(size, 0, size, r);
      ctx.lineTo(size, size - r);
      ctx.quadraticCurveTo(size, size, size - r, size);
      ctx.lineTo(r, size);
      ctx.quadraticCurveTo(0, size, 0, size - r);
      ctx.lineTo(0, r);
      ctx.quadraticCurveTo(0, 0, r, 0);
      ctx.closePath();
      ctx.clip();
    }

    // Draw image to fit square
    const imgAspect = img.naturalWidth / img.naturalHeight;
    let sw: number, sh: number, sx: number, sy: number;
    if (imgAspect > 1) {
      sh = img.naturalHeight;
      sw = sh;
      sx = (img.naturalWidth - sw) / 2;
      sy = 0;
    } else {
      sw = img.naturalWidth;
      sh = sw;
      sx = 0;
      sy = (img.naturalHeight - sh) / 2;
    }

    ctx.drawImage(img, sx, sy, sw, sh, 0, 0, size, size);

    if (cornerRadius > 0) {
      ctx.restore();
    }

    return canvas.toDataURL('image/png');
  }, [bgType, bgColor, cornerRadius]);

  useEffect(() => {
    if (!imgElement) return;
    const urls = new Map<number, string>();
    for (const size of FAVICON_SIZES) {
      urls.set(size, generateFavicon(imgElement, size));
    }
    setFaviconDataUrls(urls);
  }, [imgElement, generateFavicon]);

  const downloadSingle = useCallback((size: number) => {
    const dataUrl = faviconDataUrls.get(size);
    if (!dataUrl) return;
    const link = document.createElement('a');
    link.download = `favicon-${size}x${size}.png`;
    link.href = dataUrl;
    link.click();
  }, [faviconDataUrls]);

  const downloadAll = useCallback(() => {
    let i = 0;
    faviconDataUrls.forEach((dataUrl, size) => {
      setTimeout(() => {
        const link = document.createElement('a');
        link.download = `favicon-${size}x${size}.png`;
        link.href = dataUrl;
        link.click();
      }, i * 200);
      i++;
    });
  }, [faviconDataUrls]);

  const handleReset = useCallback(() => {
    setFile(null);
    setImgElement(null);
    setImageDimensions(null);
    setFaviconDataUrls(new Map());
    setBgType('transparent');
    setCornerRadius(0);
    setError(null);
  }, []);

  return (
    <div className="max-w-6xl mx-auto p-4 sm:p-6">
      <div className="mb-6">
        <div className="flex items-center gap-2 mb-2">
          <Globe className="w-6 h-6 text-cyan-600" />
          <h1 className="text-2xl font-bold text-gray-900">Favicon Generator</h1>
        </div>
        <p className="text-gray-600">Generate favicons in all standard sizes from any image.</p>
      </div>

      {error && (
        <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg flex items-center gap-2 text-red-700">
          <AlertCircle className="w-4 h-4 flex-shrink-0" />
          <span className="text-sm">{error}</span>
        </div>
      )}

      {!file ? (
        <div
          onDrop={handleDrop}
          onDragOver={handleDragOver}
          onClick={() => fileInputRef.current?.click()}
          className="border-2 border-dashed border-gray-300 rounded-xl p-12 text-center cursor-pointer hover:border-cyan-400 hover:bg-cyan-50/50 transition-colors"
        >
          <Upload className="w-12 h-12 text-gray-400 mx-auto mb-4" />
          <p className="text-lg font-medium text-gray-700 mb-1">Drop an image here or click to upload</p>
          <p className="text-sm text-gray-500">Supports PNG, JPG, WebP — any size, will be resized</p>
          <input
            ref={fileInputRef}
            type="file"
            accept="image/*"
            className="hidden"
            onChange={(e) => handleFileSelect(e.target.files)}
          />
        </div>
      ) : (
        <div className="space-y-6">
          {/* Controls */}
          <div className="bg-white border border-gray-200 rounded-xl p-4 sm:p-6 space-y-5">
            <div className="flex items-center justify-between">
              <h2 className="font-semibold text-gray-900">Settings</h2>
              <button onClick={handleReset} className="flex items-center gap-1 text-sm text-gray-500 hover:text-gray-700">
                <RotateCcw className="w-4 h-4" /> Reset
              </button>
            </div>

            {/* Background */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Background</label>
              <div className="flex gap-2 items-center">
                {(['transparent', 'white', 'custom'] as BackgroundType[]).map((b) => (
                  <button
                    key={b}
                    onClick={() => setBgType(b)}
                    className={`px-3 py-2 rounded-lg border text-sm capitalize transition-colors ${
                      bgType === b
                        ? 'border-cyan-500 bg-cyan-50 text-cyan-700'
                        : 'border-gray-200 hover:border-gray-300 text-gray-600'
                    }`}
                  >
                    {b}
                  </button>
                ))}
                {bgType === 'custom' && (
                  <input type="color" value={bgColor} onChange={(e) => setBgColor(e.target.value)} className="w-10 h-10 rounded border border-gray-300 cursor-pointer" />
                )}
              </div>
            </div>

            {/* Corner rounding */}
            <div>
              <div className="flex justify-between items-center mb-2">
                <label className="text-sm font-medium text-gray-700">Corner Rounding</label>
                <span className="text-sm font-mono text-cyan-600">{cornerRadius}%</span>
              </div>
              <input
                type="range"
                min={0}
                max={100}
                value={cornerRadius}
                onChange={(e) => setCornerRadius(Number(e.target.value))}
                className="w-full accent-cyan-600"
              />
              <div className="flex justify-between text-xs text-gray-400 mt-1">
                <span>Square</span>
                <span>Circle</span>
              </div>
            </div>
          </div>

          {/* Image info */}
          {imageDimensions && file && (
            <div className="flex flex-wrap gap-4 text-sm text-gray-600">
              <span className="flex items-center gap-1"><ImageIcon className="w-4 h-4" /> Source: {imageDimensions.w} x {imageDimensions.h}px</span>
              <span>{formatFileSize(file.size)}</span>
            </div>
          )}

          {/* Browser tab mockup */}
          {faviconDataUrls.has(16) && (
            <div>
              <h3 className="text-sm font-medium text-gray-700 mb-2">Browser Tab Preview</h3>
              <div className="bg-gray-200 rounded-t-lg p-0 max-w-md">
                <div className="flex items-center gap-2 bg-gray-100 rounded-t-lg px-3 py-2 border-b border-gray-200">
                  <div className="flex gap-1.5">
                    <div className="w-3 h-3 rounded-full bg-red-400" />
                    <div className="w-3 h-3 rounded-full bg-yellow-400" />
                    <div className="w-3 h-3 rounded-full bg-green-400" />
                  </div>
                  <div className="flex items-center gap-2 bg-white rounded px-3 py-1 text-xs text-gray-500 flex-1 ml-2">
                    <img src={faviconDataUrls.get(16)} alt="favicon" className="w-4 h-4" />
                    <span>My Website — example.com</span>
                  </div>
                </div>
                <div className="h-16 bg-white rounded-b-lg" />
              </div>
            </div>
          )}

          {/* All sizes grid */}
          <div>
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-sm font-medium text-gray-700">All Sizes</h3>
              <button
                onClick={downloadAll}
                className="flex items-center gap-1 px-4 py-2 bg-cyan-600 text-white rounded-lg hover:bg-cyan-700 transition-colors text-sm font-medium"
              >
                <DownloadCloud className="w-4 h-4" /> Download All
              </button>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-7 gap-4">
              {FAVICON_SIZES.map((size) => {
                const dataUrl = faviconDataUrls.get(size);
                return (
                  <div key={size} className="bg-white border border-gray-200 rounded-lg p-3 text-center group">
                    <div
                      className="mx-auto mb-2 flex items-center justify-center"
                      style={{
                        width: Math.min(size, 80),
                        height: Math.min(size, 80),
                        backgroundImage: 'linear-gradient(45deg, #e5e5e5 25%, transparent 25%), linear-gradient(-45deg, #e5e5e5 25%, transparent 25%), linear-gradient(45deg, transparent 75%, #e5e5e5 75%), linear-gradient(-45deg, transparent 75%, #e5e5e5 75%)',
                        backgroundSize: '8px 8px',
                        backgroundPosition: '0 0, 0 4px, 4px -4px, -4px 0px',
                      }}
                    >
                      {dataUrl && (
                        <img
                          src={dataUrl}
                          alt={`${size}x${size}`}
                          style={{
                            width: Math.min(size, 80),
                            height: Math.min(size, 80),
                            imageRendering: size <= 32 ? 'pixelated' : 'auto',
                          }}
                        />
                      )}
                    </div>
                    <div className="text-xs font-medium text-gray-700">{size}x{size}</div>
                    <button
                      onClick={() => downloadSingle(size)}
                      className="mt-1 text-xs text-cyan-600 hover:text-cyan-700 opacity-0 group-hover:opacity-100 transition-opacity flex items-center gap-1 mx-auto"
                    >
                      <Download className="w-3 h-3" /> Download
                    </button>
                  </div>
                );
              })}
            </div>
          </div>

          {/* HTML code snippet */}
          <div className="bg-gray-900 rounded-lg p-4">
            <h3 className="text-sm font-medium text-gray-300 mb-2">HTML Code</h3>
            <pre className="text-xs text-green-400 overflow-x-auto">
{`<link rel="icon" type="image/png" sizes="16x16" href="/favicon-16x16.png">
<link rel="icon" type="image/png" sizes="32x32" href="/favicon-32x32.png">
<link rel="icon" type="image/png" sizes="48x48" href="/favicon-48x48.png">
<link rel="apple-touch-icon" sizes="192x192" href="/favicon-192x192.png">
<link rel="icon" type="image/png" sizes="512x512" href="/favicon-512x512.png">`}
            </pre>
          </div>

          {/* Download all */}
          <div className="flex gap-3">
            <button
              onClick={downloadAll}
              className="flex items-center gap-2 px-6 py-3 bg-cyan-600 text-white rounded-lg hover:bg-cyan-700 transition-colors font-medium"
            >
              <DownloadCloud className="w-5 h-5" /> Download All Sizes
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default FaviconGenerator;
